// Validador único de fecha para los 16 caminos de "Futuros: reagendar" (Fase 5, Bloque B — diseño en
// docs/decisiones/2026-09-26-fase-5-diseno-unificacion-fechas.md). Reemplaza la validación de fecha
// que hoy vive DUPLICADA/AUSENTE en cada modal viejo (10.1) — cada writer de fecha llama a esta
// función DENTRO de su propio cuerpo, antes de tocar la DB (10.190 aplicado al frontend: el candado
// vive en el punto de escritura, nunca en la disciplina de cada pantalla que lo invoca).
//
// Principio (definido por Ignacio): en Futuros NUNCA se registra un pago real — el pago real entra
// SOLO cuando se vincula un movimiento bancario. Toda fecha editable acá es una ESTIMACIÓN, de uno de
// dos tipos:
//
//   MODO ESTRICTO — "reagendar": mover una fecha que YA EXISTE, a cuándo esperamos pagar o cobrar.
//     - Piso: no antes de hoyDelPais(pais) (huso real de Chile/Perú, nunca el navegador).
//     - No sábado ni domingo (esDiaHabil).
//
//   MODO DATO_DOCUMENTO — "registrar lo que dice un documento o un acuerdo": vencimiento de una
//   factura, cuotas de un plan pactado, o dar de alta algo nuevo.
//     - Permite fecha pasada Y fin de semana — la fecha viene de un documento o acuerdo real, no de
//       una decisión operativa de cuándo agendar un pago.
//
// Regla de corte para cualquier caso nuevo que no esté ya asignado en el diseño: CREAR o ARMAR algo
// nuevo = DATO_DOCUMENTO; MOVER una fecha puntual que ya existe = ESTRICTO. Un caso real que no encaje
// limpio en esta regla se reporta, no se decide solo.
//
// El chequeo de "¿esta fila/cuota ya tiene un evento vigente en el libro (vinculada a un movimiento
// bancario real)?" NO vive acá — es un problema de ESTADO, no de fecha, y aplica IGUAL en los dos
// modos (Ignacio). Sigue siendo el predicado `esCuotaReagendable` (cuotaReagendable.js) para cuotas de
// factura/CapEx — y para esos dos casos puntuales la garantía real ya está DENTRO de la RPC
// (`reagendar_cuota_factura`/`reagendar_cuota_activo`, con `SELECT ... FOR UPDATE`), no se duplica acá.
//
// El orden FOB ≤ Transporte < Aduana de un lote CapEx sigue siendo `validarOrdenFechasLote`
// (`loteCostos.js`), una validación APARTE — compara VARIAS fechas del mismo lote entre sí, no una
// fecha sola contra "hoy". Un lote pasa por las DOS validaciones (DATO_DOCUMENTO + validarOrdenFechasLote).
import { esDiaHabil, hoyDelPais } from "./fechasFuturosPanel";

export const MODOS_VALIDACION = Object.freeze({
  ESTRICTO: "estricto",
  DATO_DOCUMENTO: "dato_documento",
});

const FORMATO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

// Países con huso operativo conocido (10.53) — los únicos para los que `hoyDelPais` tiene un piso
// real. Fuera de este set, en modo ESTRICTO, NO hay a qué huso anclar el piso: `hoyDelPais` cae al
// huso del NAVEGADOR (fallback documentado en fechasFuturosPanel.js), que es exactamente lo que
// 10.269/10.28 prohíben usar como fuente de un hecho financiero. F4 (B.2b, 2026-09-26): rechazar
// explícito en vez de heredar ese fallback en silencio (10.177).
const PAISES_CON_HUSO_CONOCIDO = new Set(["Chile", "Perú"]);

/**
 * @param {Object} params
 * @param {string|null|undefined} params.nuevaFecha - "YYYY-MM-DD", o null/vacío si se está BORRANDO
 *   un override (restaurar automática/original) — en ese caso siempre {ok:true}, en CUALQUIER modo,
 *   sin evaluar nada más (no hay fecha que validar).
 * @param {string} params.pais - "Chile"|"Perú" (10.53). Solo se usa en modo ESTRICTO (piso de hoy).
 * @param {string} [params.modo] - uno de MODOS_VALIDACION; default ESTRICTO.
 * @returns {{ok: boolean, motivo?: string}}
 */
export const validarFechaDocumento = ({ nuevaFecha, pais, modo = MODOS_VALIDACION.ESTRICTO }) => {
  if (!nuevaFecha) return { ok: true };

  if (!FORMATO_FECHA.test(String(nuevaFecha).slice(0, 10))) {
    return { ok: false, motivo: `Fecha con formato inválido: "${nuevaFecha}".` };
  }

  if (modo === MODOS_VALIDACION.DATO_DOCUMENTO) {
    return { ok: true };
  }

  // ESTRICTO (default) — mismo orden y mismos mensajes que ya usaba reagendarEntryFuturo.js, para no
  // cambiar comportamiento en el camino que ya lo validaba (10.1: se reusa el criterio, no se inventa
  // uno nuevo).
  //
  // F4 (B.2b, 2026-09-26): país fuera de {Chile, Perú} rechaza EXPLÍCITO — antes caía en silencio al
  // fallback de huso del navegador de `hoyDelPais` (10.177/10.269).
  if (!PAISES_CON_HUSO_CONOCIDO.has(pais)) {
    return { ok: false, motivo: `País desconocido para validar fecha en modo estricto: "${pais}".` };
  }
  const fechaCorta = String(nuevaFecha).slice(0, 10);
  if (fechaCorta < hoyDelPais(pais)) {
    return { ok: false, motivo: "La fecha no puede ser anterior a hoy." };
  }
  if (!esDiaHabil(fechaCorta)) {
    return { ok: false, motivo: "Los pagos y cobros se agendan de lunes a viernes." };
  }
  return { ok: true };
};

// ─── F1 (B.2b, 2026-09-26) — candado de escritura para los writers de bajo nivel ──────────────────
//
// Cada writer compartido (activos.js, lotesCompra.js, loteCostos.js, compromisosPago.js,
// planPagosEdificios.js, guardarFechaFactura.js, planCobros.js, planPagosActivos.js,
// planPagosCompromiso.js) que escribe una columna de fecha del sistema llama a
// `exigirFechaValida`/`exigirFechaValidaEnLista` ANTES de tocar la DB. Si el payload trae la
// columna, exige un `modo` explícito (MODOS_VALIDACION) — SIN modo, lanza un error claro, nunca un
// default silencioso (10.177). El candado vive acá, en UN lugar (10.1), no repetido en cada writer.

/**
 * @param {Object} payload - el objeto que se va a insertar/actualizar (p.ej. `cambios`).
 * @param {string} columna - la key de la fecha dentro de `payload` (p.ej. "fecha_planificada").
 * @param {Object} [opts]
 * @param {string} [opts.modo] - uno de MODOS_VALIDACION. REQUERIDO si `payload[columna]` trae una
 *   fecha real. Un valor null/vacío (borrar override — "no hay ninguna fecha que validar, en ningún
 *   modo", §1 del diseño) NO exige modo: no hay nada que este candado deba validar.
 * @param {string} [opts.pais] - solo relevante en modo ESTRICTO.
 * @throws {Error} si `payload[columna]` trae una fecha sin `modo`, o si la fecha no pasa la validación.
 */
export const exigirFechaValida = (payload, columna, { modo, pais } = {}) => {
  if (!payload || !(columna in payload) || !payload[columna]) return;
  if (!modo || !Object.values(MODOS_VALIDACION).includes(modo)) {
    throw new Error(
      `Falta el modo de validación (MODOS_VALIDACION) para escribir "${columna}" — nunca un default silencioso (10.177).`
    );
  }
  const resultado = validarFechaDocumento({ nuevaFecha: payload[columna], pais, modo });
  if (!resultado.ok) {
    throw new Error(resultado.motivo);
  }
};

/**
 * Espejo de `exigirFechaValida` para escrituras en LOTE (arrays de filas — cuotas de un plan,
 * componentes de un lote): valida `columna` en CADA elemento de `lista`.
 * @param {Array<Object>} lista
 * @param {string} columna
 * @param {Object} [opts] - ver `exigirFechaValida`.
 */
export const exigirFechaValidaEnLista = (lista, columna, opts = {}) => {
  for (const fila of lista || []) {
    exigirFechaValida(fila, columna, opts);
  }
};
